import { BaseText, Meta, theme, ThreeDotLoader } from 'aimo-ui';
import AimoPlusIcon from 'components/account/subscription/AimoPlusIcon';
import Icon from 'components/icons/Icon';
import PriceEstimateContext from 'context/PriceEstimateContext';
import useGetPoolingInfo from 'hooks/useGetPoolingInfo';
import useSelectedCompanyBenefit from 'hooks/useSelectedCompanyBenefit';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import styled from 'styled-components';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { CountryCode } from 'types/generatedSchemaTypes';
import { currencyFormatter } from 'utils/commonUtils';
import { isWeb } from 'utils/deviceUtils';
import PreloadingWrapper from './PreloadingWrapper';
import { useUiStore } from 'stores/uiStore';

const BoldBaseText = styled(BaseText)`
  font-family: ${theme.fontFamily.bold};
  font-size: ${theme.normalising.fontPixel(16)}px;
`;

interface PriceEstimateProps {
  darkMode?: boolean;
  parkingZoneCountryCode: CountryCode;
  testID?: string;
  parkingZoneUid: string;
}

const PriceEstimate: React.FC<PriceEstimateProps> = ({
  darkMode = false,
  parkingZoneCountryCode,
  testID,
  parkingZoneUid,
}) => {
  const { t } = useTranslation();

  const { poolingGroup } = useGetPoolingInfo(parkingZoneUid);

  const lastValidPriceRef = useRef<string>('');

  const { loadingPrice, priceEstimate, error, startButtonOpacity } =
    useContext(PriceEstimateContext);

  const { parkingConditions } = useParkingStore((state) => state);
  const { hasCorporateAccount } = useSelectedCompanyBenefit(parkingZoneUid);

  useEffect(() => {
    useParkzoneStore.getState().setNewParkingSessionPriceBreakdownValues({
      priceEstimate: priceEstimate,
      parkingZoneUid,
      parkingZoneCountryCode: parkingConditions.parkingZoneCountryCode,
    });
  }, [
    priceEstimate,
    parkingZoneUid,
    parkingZoneCountryCode,
    parkingConditions.parkingZoneCountryCode,
  ]);

  const estimate = priceEstimate?.totalAmount;

  const pricingInfo = useMemo(() => {
    if (error || loadingPrice) {
      return lastValidPriceRef.current || '';
    }

    const currentPrice =
      typeof estimate === 'number'
        ? `${currencyFormatter(estimate ?? 0, parkingZoneCountryCode)}`
        : t('price.noPrice');

    if (estimate) {
      lastValidPriceRef.current = currentPrice;
    }
    return currentPrice;
  }, [estimate, parkingZoneCountryCode, loadingPrice, error, t]);

  if (poolingGroup) {
    return (
      <Meta
        center
        testID={`${testID}-pooling-prices`}
        color={darkMode ? theme.colors.white : theme.colors.gray300}>
        {t('permit.access.priceDependsPooling')}
      </Meta>
    );
  }

  return (
    <Pressable
      onPress={() => {
        useUiStore
          .getState()
          .setSheetModal('NewParkingSessionPriceBreakdownDetails');
      }}>
      <View style={styleSheet.container}>
        <PreloadingWrapper
          firstItem={
            <View>
              <BoldBaseText
                center
                color={darkMode ? theme.colors.white : theme.colors.black}
                testID={`${testID}-value`}>
                {pricingInfo}
              </BoldBaseText>
            </View>
          }
          secondItem={
            <ColumnWithMargins
              mt={isWeb ? 40 : theme.normalising.heightPixel(5)}>
              <ThreeDotLoader sharedValue={startButtonOpacity} />
            </ColumnWithMargins>
          }
        />
        <AimoPlusIcon hasCorporateAccount={hasCorporateAccount} />
        <Icon color={theme.colors.boost.green} name="Info-Outlined" />
      </View>
    </Pressable>
  );
};

export default PriceEstimate;

const styleSheet = StyleSheet.create({
  container: {
    height: theme.normalising.heightPixel(40),
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
});
